import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SERVICES_DATA } from "@/lib/services-data";
import { ServiceVideoCarousel } from "./ServiceVideoCarousel";
import { Skeleton } from "@/components/ui/skeleton";

interface PreviewVideo {
  id: string;
  service_id: string;
  video_url: string;
  poster_url: string | null;
  start_time: number;
  end_time: number | null;
  display_order: number;
  source_type?: "upload" | "youtube" | "external";
}

interface CMSService {
  id: string;
  slug: string;
  title: string;
  short_description: string | null;
  icon_name: string;
  image_url: string | null;
  is_active: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

// Dynamic icon loader
import * as LucideIcons from "lucide-react";
import { LucideIcon } from "lucide-react";

const getIconByName = (name: string): LucideIcon => {
  const icons = LucideIcons as unknown as Record<string, LucideIcon>;
  return icons[name] || LucideIcons.Camera;
};

// Skeleton card for loading state
function ServiceCardSkeleton() {
  return (
    <div className="relative h-80 rounded-lg overflow-hidden bg-muted">
      {/* Shimmer background */}
      <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted-foreground/5 to-muted animate-pulse" />
      
      {/* Content skeleton */}
      <div className="absolute inset-0 p-6 flex flex-col justify-end">
        <div className="mb-3">
          {/* Icon skeleton */}
          <Skeleton className="w-12 h-12 rounded-full mb-4" />
          {/* Title skeleton */}
          <Skeleton className="h-6 w-3/4 mb-2" />
          {/* Description skeleton */}
          <Skeleton className="h-4 w-full mb-1" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>
    </div>
  );
}

interface ServiceCardProps {
  service: typeof SERVICES_DATA[0];
  videos: PreviewVideo[];
  cmsService?: CMSService;
}

function ServiceCard({ service, videos, cmsService }: ServiceCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  const Icon = cmsService?.icon_name 
    ? getIconByName(cmsService.icon_name) 
    : service.icon;
  
  const title = cmsService?.title || service.title;
  const description = cmsService?.short_description || service.shortDescription;
  const fallbackImage = cmsService?.image_url || service.image;
  const href = `/services/${cmsService?.slug || service.slug}`;

  return (
    <motion.div variants={itemVariants}>
      <Link
        to={href}
        className="group block relative h-80 rounded-lg overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Video Carousel or Fallback Image */}
        {/* To enable debug overlay, add debug={true} to ServiceVideoCarousel */}
        {videos.length > 0 ? (
          <ServiceVideoCarousel
            videos={videos}
            fallbackImage={fallbackImage}
            isHovered={isHovered}
            className="absolute inset-0"
            // debug={true} // Uncomment to show video playback debug overlay
          />
        ) : (
          <div
            className={`absolute inset-0 bg-cover bg-center transition-transform duration-700 ${
              isHovered ? "scale-110" : "scale-100"
            }`}
            style={{ backgroundImage: `url('${fallbackImage}')` }}
          />
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
        
        {/* Content */}
        <div className="absolute inset-0 p-6 flex flex-col justify-end">
          <div className="mb-3">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mb-4 group-hover:bg-primary/30 transition-colors">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
              {title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {description}
            </p>
          </div>
          
          <span className="text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Learn More →
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export function ServicesSection() {
  // Fetch CMS services
  const { data: cmsServices, isLoading: isLoadingServices } = useQuery({
    queryKey: ["cms-services-section"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_services")
        .select("id, slug, title, short_description, icon_name, image_url, is_active")
        .eq("is_active", true)
        .order("display_order");
      
      if (error) throw error;
      return data as CMSService[];
    },
  });

  // Fetch all preview videos
  const { data: previewVideos, isLoading: isLoadingVideos } = useQuery({
    queryKey: ["service-preview-videos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_service_preview_videos")
        .select("*")
        .eq("is_active", true)
        .order("display_order");
      
      if (error) throw error;
      return data as PreviewVideo[];
    },
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const isLoading = isLoadingServices || isLoadingVideos;

  // Create a map of service slug to CMS data
  const cmsServiceMap = new Map(
    (cmsServices || []).map((s) => [s.slug, s])
  );

  // Create a map of service_id to videos
  const videosMap = new Map<string, PreviewVideo[]>();
  (previewVideos || []).forEach((video) => {
    const existing = videosMap.get(video.service_id) || [];
    videosMap.set(video.service_id, [...existing, video]);
  });

  // Get videos for a service by slug (need to find CMS service id first)
  const getVideosForService = (slug: string): PreviewVideo[] => {
    const cmsService = cmsServiceMap.get(slug);
    if (!cmsService) return [];
    return videosMap.get(cmsService.id) || [];
  };

  return (
    <section id="services" className="py-24 bg-background">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-[0.3em] uppercase">
            What We Do
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold mt-4 mb-6">
            Our Services
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            From intimate weddings to large-scale brand campaigns, we bring cinematic 
            excellence to every project we undertake.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {isLoading
            ? Array.from({ length: 8 }).map((_, index) => (
                <ServiceCardSkeleton key={index} />
              ))
            : SERVICES_DATA.map((service) => (
                <ServiceCard
                  key={service.slug}
                  service={service}
                  videos={getVideosForService(service.slug)}
                  cmsService={cmsServiceMap.get(service.slug)}
                />
              ))}
        </motion.div>
      </div>
    </section>
  );
}
